import _ from "lodash";
import { newGuid } from "../../../util/types/guid";

interface CallMessage {
	guid: string;
	type: string;
	action: string;
	data: any;
}

interface Call {
	time: number;
	message: CallMessage;
	callback: (data?: any) => void;
	exceptionCallback: (data?: any) => void;
}

interface WebSocketMessage {
	action: string;
	type: string;
	data: any;
	guid: string;
}

export interface IDobcoSocket {
	isRegisterCalled: boolean;
	pageId: string;
	registerCallback(action: DobcoSocketActions, callback: Function): void;
	send(action: DobcoSocketActions, parameters: any, callback?: Function, type?: DobcoSocketMessageType, guid?: string, exceptionCallback?: Function): void;
	sendAsync(action: DobcoSocketBrowserToPhoneActions, parameters: any): Promise<any>;
	onOpen: (() => void) | null;
	onClose: (() => void) | null;
	onError: (() => void) | null;
}

export class DobcoSocket implements IDobcoSocket {
	private readonly address: string;
	private readonly POWHomerBGuid: string;
	public pageId: string;
	private readonly tblUser_UID: number;
	private websocket: WebSocket | null = null;
	private readonly calls: Call[] = [];
	private callbacks: Record<string, (data: any) => void> = {};
	private readonly callReturns: Call[] = [];
	public isRegisterCalled = false;
	private readonly _registerCallback: () => void;

	public onOpen: (() => void) | null = null;
	public onClose: (() => void) | null = null;
	public onError: (() => void) | null = null;
	private readonly browserKey: string;

	constructor(address: string, POWHomerBGuid: string, pageId: string, registerCallback: () => void, tblUser_UID: number) {
		this.address = address;
		this.POWHomerBGuid = POWHomerBGuid;
		this.pageId = pageId;
		this.tblUser_UID = tblUser_UID;
		this.browserKey = `${this.POWHomerBGuid}_${this.tblUser_UID}`;
		this._registerCallback = registerCallback;

		this.init();
	}

	private init(): void {
		this.websocket = new WebSocket(this.address);

		this.websocket.onopen = () => {
			this._register();
			if (this.onOpen) this.onOpen();
		};

		this.websocket.onmessage = (e: MessageEvent) => {
			const data: WebSocketMessage = JSON.parse(e.data);

			if (data.action === "pingPong" && data.type === "call") {
				this.send("pingPong", data.data, undefined, "return", data.guid);
				return;
			}

			if (data.type === "call") {
				const newdata = { ...data.data, original: data };
				const callback = this.callbacks[data.action];
				if (!callback) {
					console.error(`No DobcoSocket callback registered for ${data.action}`);
					return;
				}
				callback(newdata);
			} else if (data.type === "exception") {
				const calls = _.remove(this.callReturns, c => c.message.guid === data.guid);
				_.invokeMap(calls, "exceptionCallback", data.data);
				_.invokeMap(calls, "callback", data.data);
			} else {
				const calls = _.remove(this.callReturns, c => c.message.guid === data.guid);
				_.invokeMap(calls, "callback", data.data);
			}
		};

		this.websocket.onclose = (e: CloseEvent) => {
			if (e.code !== 1000) setTimeout(() => this.init(), 1000);
			if (this.onClose) this.onClose();
		};

		this.websocket.onerror = (e: Event) => {
			console.error("🏠 Homereading websocket error: ", e);
		};
	}

	public registerCallback(action: string, callback: (data: any) => void): void {
		this.callbacks[action] = callback;
	}

	private _processCalls(): void {
		// Remove callReturns older than 5 minutes
		_.remove(this.callReturns, n => n.time < Date.now() - 5 * 60 * 1000);

		while (this.websocket && this.websocket.readyState === WebSocket.OPEN && this.calls.length > 0) {
			const call = this.calls.shift()!;
			try {
				this.websocket.send(JSON.stringify(call.message));
				this.callReturns.push(call);
			} catch (e) {
				this.calls.unshift(call);
			}

			if (!this.isRegisterCalled && call.message.action === "register") {
				this.isRegisterCalled = true;
				this._registerCallback();
			}
		}
	}

	private _register(): void {
		this.send("register", { browserKey: this.browserKey, pageID: this.pageId });
	}

	public send(
		action: string,
		parameters: any,
		callback: (data?: any) => void = () => {},
		type: string = "call",
		guid: string = newGuid(),
		exceptionCallback: (data?: any) => void = () => {},
	): void {
		const call: Call = {
			time: Date.now(),
			message: { guid, type, action, data: parameters },
			callback,
			exceptionCallback,
		};

		this.calls.push(call);
		this._processCalls();
	}

	public sendAsync(action: string, parameters: any): Promise<any> {
		return new Promise((resolve, reject) => {
			const call: Call = {
				time: Date.now(),
				message: {
					guid: newGuid(),
					type: "call",
					action,
					data: parameters,
				},
				callback: resolve,
				exceptionCallback: reject,
			};

			this.calls.push(call);
			this._processCalls();
		});
	}
}
